import React, { useState } from 'react'; 
import { useLocalizedStrings } from '../../Localization';
import { Button, Heading, Icon, NumberDisplay } from '@aidkitorg/component-library';
import { usePayments } from '../../Contexts/PaymentsContext';
import { PaymentBatchRecipientsDataTable } from './components/PaymentBatchRecipientsDataTable';
import { useHistory, useParams } from 'react-router-dom';
import { generateTimestampString } from '../../utils/adminPanel.utils';
import { PaymentBatchDownloadModal } from './components/PaymentBatchDownloadModal';
import { AdminPanelPage } from '../AdminPanelPage';

export const PaymentBatchDetailsPage = () => {
  const { batch_name } = useParams<{ batch_name: string }>()

  const [downloadModalOpen, setDownloadModalOpen] = useState<boolean>(false)

  const L = useLocalizedStrings()
  const { 
    fetchRecipientDetailsByBatch,
    getBatchTimestamp, 
    getBatchNumRecipients, 
    getBatchName,
    getBatchNumFailedPayments,

    getIsBatchLoading,
  } = usePayments();
  
  const history = useHistory()

  const paymentBatchTimestamp = getBatchTimestamp(batch_name)
  const paymentBatchNumRecipients = getBatchNumRecipients(batch_name)
  const paymentBatchName = getBatchName(batch_name)
  const paymentBatchNumFailedPayments = getBatchNumFailedPayments(batch_name)

  const isPaymentBatchLoading = getIsBatchLoading(batch_name)

  const totalRecipientsCard = <NumberDisplay 
    title={L.admin_panel.payments.total_recipients}
    value={paymentBatchNumRecipients}
    iconName="UsersIcon"
    iconColor='blue'
    loading={isPaymentBatchLoading}
  />

  const numFailedPaymentsCard = <NumberDisplay
    title={L.admin_panel.payments.failed_payments}
    value={paymentBatchNumFailedPayments}
    iconName="XCircleIcon"
    iconColor='red'
    loading={isPaymentBatchLoading}
  />

  const lastUpdated = (
    <div className="flex gap-2 items-center text-gray-700">
      {paymentBatchTimestamp && (
        <div className="flex gap-1 items-center">
          <div>
            <Icon role="decorative" name="ClockIcon"/>
          </div>
          <div>
            {L.admin_panel.payments.last_updated}: {generateTimestampString(paymentBatchTimestamp)}
          </div>
        </div>
      )}
      <div>
        <Button 
          icon={<Icon role="decorative" name="ArrowPathIcon" />} 
          variant="unstyled" size="sm" 
          aria-label="Refresh data"
          onClick={async () => {
            await fetchRecipientDetailsByBatch(batch_name, true)
          }}
        />
      </div>
    </div>
  )

  const breadcrumbs = (
    <>
      <Button 
        variant="link"
        onClick={(e) => {
          history.push('/admin-panel/payments')
          e.preventDefault()
        }}
      >
        {L.admin_panel.nav.payments}
      </Button>
      <Icon size="sm" role="decorative" name="ChevronRightIcon"/>
      <Button 
        variant="link"
        onClick={(e) => {
          history.push('/admin-panel/payments/payment-details/'+batch_name)
          e.preventDefault()
        }}
      >
        {L.admin_panel.nav.payment_details}
      </Button>
    </>
  )

  const headerContent = (
    <>
      <div>
        {!isPaymentBatchLoading && (
          <>
            <Heading level="h1" className="m-0">{paymentBatchName}</Heading>
            <div>
              <div className="inline-block text-lg text-gray-700">{L.admin_panel.payments.payment_details_desc}</div>
            </div>
          </>
        )}
      </div>
      <div className="flex flex-col gap-y-2">
        {lastUpdated}
        {!isPaymentBatchLoading && (
          <div className="flex justify-end">
            <Button onClick={() => setDownloadModalOpen(true)}>
              <div className="flex gap-2 items-center">
                <Icon name="ArrowDownTrayIcon" role="decorative" />
                <div>{L.admin_panel.payments.export_recipients}</div>
              </div>
            </Button>
            <PaymentBatchDownloadModal batchName={batch_name} downloadModalOpen={downloadModalOpen} setDownloadModalOpen={setDownloadModalOpen} />
          </div>
        )}
      </div>
    </>
  )
  
  return (
    <AdminPanelPage 
      headerContent={headerContent}
      breadcrumbs={breadcrumbs}
    >
      <div className="flex flex-col gap-4">
        <div className="flex gap-2">
          <div>
            {totalRecipientsCard}
          </div>
          <div>
            {numFailedPaymentsCard}
          </div>

        </div>
        <PaymentBatchRecipientsDataTable />
      </div>
    </AdminPanelPage>
  )
}